import { ProfileEditor } from '../curves/ProfileEditor.js';
import type { Vec2 } from '../curves/curve-math.js';
import type { ShapeEditorProps } from './doc-context.js';
import { arr, Bool, bool, Num, num, patch, Section } from './fields.js';

/** Points stored as `[[a, b], ...]` → typed 2D points (bad entries dropped). */
export function points2(v: unknown): Vec2[] {
  return arr<unknown>(v).flatMap((p) => (Array.isArray(p) && typeof p[0] === 'number' && typeof p[1] === 'number' ? [[p[0], p[1]] as Vec2] : []));
}

/** `lathe`: a `[radius, y]` profile revolved around +Y. */
export function LatheEditor({ shape, onChange }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  return (
    <>
      <Section title="Profile" hint="Profile [radius, y] points revolved around +Y (radius ≥ 0; the dashed mirror is the silhouette).">
        <ProfileEditor
          lathe
          points={points2(shape.points)}
          smooth={bool(shape.smooth) ?? false}
          samples={num(shape.samples) ?? 6}
          onChange={(p) => set('points', p)}
        />
      </Section>
      <Num label="Segments" hint="Steps around the axis." integer min={3} value={num(shape.segments)} fallback={24} onChange={(v) => set('segments', v)} />
      <Bool label="Smooth" hint="Smooth curve through the profile points (centripetal Catmull-Rom)." value={bool(shape.smooth)} onChange={(v) => set('smooth', v)} />
      <Num label="Samples" hint="Curve samples per profile span when smooth." integer min={1} max={64} value={num(shape.samples)} fallback={6} onChange={(v) => set('samples', v)} />
    </>
  );
}

/** `extrude`: a closed XY outline pushed along Z. */
export function ExtrudeEditor({ shape, onChange }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  return (
    <>
      <Section title="Outline" hint="Outline in the XY plane (any winding, may be concave).">
        <ProfileEditor
          closed
          points={points2(shape.polygon)}
          smooth={bool(shape.smooth) ?? false}
          samples={num(shape.samples) ?? 6}
          onChange={(p) => set('polygon', p)}
        />
      </Section>
      <Num label="Depth" hint="Thickness along Z, centered on the origin." min={0} value={num(shape.depth)} fallback={0.2} onChange={(v) => set('depth', v)} />
      <Num label="Bevel" hint="Rounded front and back edges (m)." min={0} value={num(shape.bevel)} fallback={0} onChange={(v) => set('bevel', v)} />
      <Bool label="Smooth" hint="Smooth closed curve through the outline points." value={bool(shape.smooth)} onChange={(v) => set('smooth', v)} />
      <Num label="Samples" hint="Curve samples per outline span when smooth." integer min={1} max={64} value={num(shape.samples)} fallback={6} onChange={(v) => set('samples', v)} />
    </>
  );
}
